<script setup lang="ts">
import { computed, ref } from 'vue';

import { ReceiptModelAPI } from '~/services/receipt-model-api/receipt-model-api.types';
import money from '~/utils/money';

import ReasonModal from '../../components/ReasonModal.vue';
import { TRANSFER_SUMMARY_CARDS, TRANSFER_TABLE_COLUMNS } from '../../constants';
import useTransferFormatters from '../../hooks/useTransferFormatters';
import useCancelTransfer from '../../queries/useCancelTransfer';
import useForcePayment from '../../queries/useForcePayment';

interface Props {
  receiptModelData: ReceiptModelAPI.ReceiptModelResponse;
}

const props = defineProps<Props>();

const {
  formatCategory,
  formatKind,
  formatRecipient,
  formatStatus,
  getStatusColor,
} = useTransferFormatters();

const summaryCards = computed(() => TRANSFER_SUMMARY_CARDS.map((card) => ({
  ...card,
  value: props.receiptModelData[card.key as keyof typeof props.receiptModelData] as number,
})));

const isForcePaymentModalVisible = ref(false);
const isCancelTransferModalVisible = ref(false);
const selectedTransferId = ref<string>('');

const forcePaymentMutation = useForcePayment();
const cancelTransferMutation = useCancelTransfer();

const canForcePayment = (status: ReceiptModelAPI.TransferStatus): boolean => {
  const allowedStatuses = ['awaiting_payment', 'fail', 'payment_blocked', 'waiting_send_payment'];
  return allowedStatuses.includes(status);
};

const canCancelTransfer = (status: ReceiptModelAPI.TransferStatus): boolean => {
  const allowedStatuses = ['fail', 'payment_blocked', 'waiting_send_payment', 'awaiting_payment'];
  return allowedStatuses.includes(status);
};

const getForcePaymentTooltipMessage = (status: ReceiptModelAPI.TransferStatus): string => {
  if (canForcePayment(status)) {
    return '';
  }
  return 'Disponível apenas quando a transferência está aguardando envio de pagamento.';
};

const getCancelTransferTooltipMessage = (status: ReceiptModelAPI.TransferStatus): string => {
  if (canCancelTransfer(status)) {
    return '';
  }
  return 'Disponível apenas quando a transferência está aguardando envio de pagamento.';
};

const getForcePaymentTooltip = (status: ReceiptModelAPI.TransferStatus): string => (
  !canForcePayment(status) ? getForcePaymentTooltipMessage(status) : ''
);

const getCancelTransferTooltip = (status: ReceiptModelAPI.TransferStatus): string => (
  !canCancelTransfer(status) ? getCancelTransferTooltipMessage(status) : ''
);

const handleForcePayment = (transferId: string) => {
  selectedTransferId.value = transferId;
  isForcePaymentModalVisible.value = true;
};

const handleCancelTransfer = (transferId: string) => {
  selectedTransferId.value = transferId;
  isCancelTransferModalVisible.value = true;
};

const handleForcePaymentConfirm = async (data: { reason: string }) => {
  try {
    await forcePaymentMutation.mutateAsync({
      reason: data.reason,
      transferId: selectedTransferId.value,
    });
    isForcePaymentModalVisible.value = false;
    selectedTransferId.value = '';
  } catch (error) {
    // Error handled by mutation
  }
};

const handleCancelTransferConfirm = async (data: { reason: string }) => {
  try {
    await cancelTransferMutation.mutateAsync({
      reason: data.reason,
      transferId: selectedTransferId.value,
    });
    isCancelTransferModalVisible.value = false;
    selectedTransferId.value = '';
  } catch (error) {
    // Error handled by mutation
  }
};

</script>

<template>
  <div class="transfers-table">
    <div class="row mb-3">
      <div v-for="card in summaryCards" :key="card.key" class="summary-card-col">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <span class="text-muted">{{ card.label }}</span>
              <span class="fw-bold" :class="card.colorClass">
                {{ money.formatBRL(card.value) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-vcenter">
        <thead>
          <tr>
            <th v-for="column in TRANSFER_TABLE_COLUMNS" :key="column.value">
              {{ column.name }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="transfer in receiptModelData.transfers" :key="transfer.id">
            <td>
              <span class="badge bg-secondary-lt">
                {{ formatRecipient(transfer.recipient) }}
              </span>
            </td>
            <td>{{ formatCategory(transfer.category) }}</td>
            <td>{{ formatKind(transfer.kind) }}</td>
            <td class="fw-bold">{{ money.formatBRL(transfer.value) }}</td>
            <td>{{ transfer.percent }}%</td>
            <td>
              <span
                :class="`badge bg-${getStatusColor(transfer.status)}-lt`"
              >
                {{ formatStatus(transfer.status) }}
              </span>
            </td>
            <td>
              <span class="text-secondary">{{ transfer.blocked_reason || '-' }}</span>
            </td>
            <td>
              <div class="d-flex gap-2">
                <div
                  v-permission="['contracts_transfer_force_payment:view']"
                  :data-tooltip="getForcePaymentTooltip(transfer.status)"
                  class="tooltip-wrapper"
                  :class="{ 'has-tooltip': !canForcePayment(transfer.status) }"
                >
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="canForcePayment(transfer.status) ? 'btn-outline-primary' : 'btn-outline-secondary'"
                    :disabled="!canForcePayment(transfer.status) || forcePaymentMutation.isPending.value"
                    @click="handleForcePayment(transfer.id)"
                  >
                    Forçar Pagamento
                  </button>
                </div>

                <div
                  v-permission="['contracts_transfer_cancel:view']"
                  :data-tooltip="getCancelTransferTooltip(transfer.status)"
                  class="tooltip-wrapper"
                  :class="{ 'has-tooltip': !canCancelTransfer(transfer.status) }"
                >
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="canCancelTransfer(transfer.status) ? 'btn-outline-danger' : 'btn-outline-secondary'"
                    :disabled="!canCancelTransfer(transfer.status) || cancelTransferMutation.isPending.value"
                    @click="handleCancelTransfer(transfer.id)"
                  >
                    Cancelar Transferência
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ReasonModal
      v-model="isForcePaymentModalVisible"
      title="Forçar Pagamento de Transferência"
      primary-button-text="Enviar"
      placeholder="Descreva o motivo pelo qual a transferência deve ser forçada..."
      field-label="Motivo"
      :is-loading="forcePaymentMutation.isPending.value"
      @confirm="handleForcePaymentConfirm"
    />

    <ReasonModal
      v-model="isCancelTransferModalVisible"
      title="Cancelar Transferência"
      primary-button-text="Cancelar Transferência"
      placeholder="Descreva o motivo pelo qual a transferência deve ser cancelada..."
      field-label="Motivo do Cancelamento"
      :is-loading="cancelTransferMutation.isPending.value"
      @confirm="handleCancelTransferConfirm"
    />
  </div>
</template>

<style scoped lang="scss">
.transfers-table {
  .summary-card-col {
    @media (min-width: 768px) {
      flex: 0 0 auto;
      width: 50%;
    }
  }

  .table {
    font-size: 0.875rem;

    th {
      font-weight: 600;
      color: #495057;
      border-bottom: 2px solid #dee2e6;
    }

    td {
      vertical-align: middle;
    }
  }

  .card {
    border: 1px solid #e3e6f0;
    border-radius: 0.35rem;

    .card-body {
      padding: 1rem;
    }
  }
}

.tooltip-wrapper {
  display: inline-block;
  position: relative;

  &.has-tooltip {
    &::before {
      content: attr(data-tooltip);
      position: absolute;
      bottom: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%);
      background-color: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 10px 14px;
      border-radius: 6px;
      font-size: 0.8rem;
      font-weight: 500;
      z-index: 1050;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;
      min-width: 200px;
      max-width: 280px;
      width: max-content;
      white-space: normal;
      text-align: center;
      line-height: 1.4;
      word-wrap: break-word;

      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

      @media (max-width: 768px) {
        max-width: 220px;
        font-size: 0.75rem;
        padding: 8px 12px;
      }
    }

    &::after {
      content: '';
      position: absolute;
      bottom: calc(100% + 2px);
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-top-color: rgba(0, 0, 0, 0.9);
      z-index: 1050;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;
    }

    &:hover {
      &::before,
      &::after {
        opacity: 1;
        visibility: visible;
      }
    }

    &:last-child {
      &::before {
        left: auto;
        right: 0;
        transform: none;
      }

      &::after {
        left: auto;
        right: 20px;
        transform: none;
      }
    }
  }
}
</style>
